import {
  AccountResponseV1,
  CreateAccountRequestV1,
  CreateGroupRequestV1,
  CreateUserAccountResponseV1,
  CreateUserAccountWithDefaultGroupRequestV1,
  CreateUserRequestV1,
  DeleteMode,
  GroupMemberResponseV1,
  GroupResponseV1,
  HouseholdMemberResponseV1,
  PermissionResponseV1,
  RoleResponseV1,
  RoleScopeResponseV1,
  ScopeResponseV1,
  SetMemberRoleRequestV1,
  UserResponseV1
} from '@access-management/access-management';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AccessManagementService {
  private readonly http = inject(HttpClient);
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private readonly accountsApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/accounts`;
  private readonly usersApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/users`;
  private readonly rolesApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/roles`;
  private readonly scopesApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/scopes`;
  private readonly groupsApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/groups`;
  private readonly householdsApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/households`;
  private readonly permissionsApiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/permissions`;

  private handleResourceError<T>(err: unknown): Observable<never> {
    const error =
      err instanceof HttpErrorResponse
        ? new Error(`HTTP ${err.status}: ${err.message}`)
        : err instanceof Error
          ? err
          : new Error(String(err));
    return throwError(() => error);
  }

  // Resource factories for per-component resource instances
  createAccountsResource() {
    return rxResource<AccountResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<AccountResponseV1[]>>(this.accountsApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createUsersResource() {
    return rxResource<UserResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<UserResponseV1[]>>(this.usersApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createGroupsResource() {
    return rxResource<GroupResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<GroupResponseV1[]>>(this.groupsApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createRolesResource() {
    return rxResource<RoleResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<RoleResponseV1[]>>(this.rolesApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createScopesResource() {
    return rxResource<ScopeResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<ScopeResponseV1[]>>(this.scopesApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createUserAccount(userAccount: CreateUserAccountWithDefaultGroupRequestV1): Observable<CreateUserAccountResponseV1> {
    return this.http
      .post<
        ServiceResponseV1<CreateUserAccountResponseV1>
      >(`${this.accountsApiUrl}/create`, userAccount, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  // ==================== ACCOUNTS ====================
  createAccount(account: CreateAccountRequestV1): Observable<AccountResponseV1> {
    return this.http
      .post<ServiceResponseV1<AccountResponseV1>>(`${this.accountsApiUrl}`, account, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  deleteAccount(accountId: string): Observable<void> {
    return this.http.delete<void>(`${this.accountsApiUrl}/${accountId}`);
  }

  // ==================== USERS ====================
  getUserById(userId: string): Observable<UserResponseV1> {
    return this.http
      .get<ServiceResponseV1<UserResponseV1>>(`${this.usersApiUrl}/${userId}`)
      .pipe(map((response) => response.data!));
  }

  createUser(user: CreateUserRequestV1): Observable<UserResponseV1> {
    return this.http
      .post<ServiceResponseV1<UserResponseV1>>(`${this.usersApiUrl}`, user, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  deleteUser(userId: string, mode: DeleteMode = 'Soft'): Observable<void> {
    return this.http.delete<void>(`${this.usersApiUrl}/${userId}?mode=${mode}`);
  }

  deleteHousehold(groupId: string, mode: DeleteMode = 'Soft'): Observable<void> {
    return this.http.delete<void>(`${this.householdsApiUrl}/${groupId}?mode=${mode}`);
  }

  getUserGroups(userId: string): Observable<GroupResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<GroupResponseV1[]>>(`${this.usersApiUrl}/${userId}/groups`)
      .pipe(map((response) => response.data || []));
  }

  getUserHousehold(userId: string): Observable<HouseholdMemberResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<HouseholdMemberResponseV1[]>>(`${this.usersApiUrl}/${userId}/household`)
      .pipe(map((response) => response.data || []));
  }

  getPermissionsGrantedBy(grantorUserId: string): Observable<PermissionResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<PermissionResponseV1[]>>(`${this.permissionsApiUrl}/grantor/${grantorUserId}`)
      .pipe(map((response) => response.data || []));
  }

  getPermissionsGrantedTo(granteeUserId: string): Observable<PermissionResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<PermissionResponseV1[]>>(`${this.permissionsApiUrl}/grantee/${granteeUserId}`)
      .pipe(map((response) => response.data || []));
  }

  // ==================== GROUPS ====================
  getGroupById(groupId: string): Observable<GroupResponseV1> {
    return this.http
      .get<ServiceResponseV1<GroupResponseV1>>(`${this.groupsApiUrl}/${groupId}`)
      .pipe(map((response) => response.data!));
  }

  createGroup(group: CreateGroupRequestV1): Observable<GroupResponseV1> {
    return this.http
      .post<ServiceResponseV1<GroupResponseV1>>(`${this.groupsApiUrl}`, group, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  renameGroup(groupId: string, name: string): Observable<GroupResponseV1> {
    return this.http
      .put<ServiceResponseV1<GroupResponseV1>>(`${this.groupsApiUrl}/${groupId}/name`, { name }, this.httpOptions)
      .pipe(
        map((response) => {
          // Thrown rather than asserted with `!`: a 200 carrying no group would otherwise reach the caller as
          // undefined and fail somewhere further away from the cause.
          if (!response.data) {
            throw new Error(`Renaming group ${groupId} returned no group`);
          }
          return response.data;
        }),
        catchError((error: HttpErrorResponse | Error) => {
          console.error('Failed to rename group', groupId, error);
          return throwError(() => error);
        })
      );
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.http.delete<void>(`${this.groupsApiUrl}/${groupId}`);
  }

  getGroupMembers(groupId: string): Observable<GroupMemberResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<GroupMemberResponseV1[]>>(`${this.groupsApiUrl}/${groupId}/members`)
      .pipe(map((response) => response.data || []));
  }

  /**
   * Access Management spells this route `removeMember({userId})`, not `members/{userId}`.
   *
   * The `members/{userId}` spelling has never existed on the engine: the console's Remove control
   * answered 404 on every use, and the screen reported it as a failed removal rather than as a route
   * that is not there.
   */
  removeMemberFromGroup(groupId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.groupsApiUrl}/${groupId}/removeMember(${userId})`);
  }

  /**
   * Moves a member onto Admin or Member, the two roles a member can hold; ownership follows the
   * household relationship.
   * <p>
   * The engine decides what a move means. Crossing onto or off Admin publishes the household admin
   * granted or revoked event, and a move between two roles that are neither publishes none, because
   * that event is what Notification turns into a toast telling the member they are now a household
   * admin. It is idempotent: a move to the role already held changes nothing and announces nothing.
   * <p>
   * Only households qualify, and the household owner's role can never be changed. Refusals arrive as
   * 400 with the reason in the body rather than as a 404, so an unknown role name is told apart from
   * an unknown member.
   * <p>
   * The member it answers with carries no scopes, like every mutation response here, so a caller
   * showing capabilities re-reads the roster rather than patching the row in place.
   */
  setMemberRole(groupId: string, userId: string, roleName: string): Observable<GroupMemberResponseV1> {
    const request: SetMemberRoleRequestV1 = { roleName };
    return this.http
      .put<
        ServiceResponseV1<GroupMemberResponseV1>
      >(`${this.groupsApiUrl}/${groupId}/members/${userId}/role`, request, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  // ==================== ROLES ====================
  // Read only. Role authoring is retired: the three seeded bundles are the whole vocabulary and the
  // engine serves no create, delete or add-scopes route to call. What the console reads is the list
  // (createRolesResource) and one role's scopes; a single role by id had no screen left to serve.
  getRoleScopes(roleId: string): Observable<RoleScopeResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<RoleScopeResponseV1[]>>(`${this.rolesApiUrl}/${roleId}/scopes`)
      .pipe(map((response) => response.data || []));
  }
}
